// Small, honest status chip: shows how many tools this page exposes to an
// in-browser AI agent, and whether the browser speaks WebMCP natively.

import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useWebMcpState } from "@/lib/webmcp/use-webmcp";

const LABEL: Record<string, string> = {
  native: "Native WebMCP",
  polyfill: "WebMCP (polyfill)",
  unavailable: "WebMCP unavailable",
  unknown: "Checking…",
};

export function WebMcpIndicator({ className = "" }: { className?: string }) {
  const { availability, tools } = useWebMcpState();
  const [open, setOpen] = useState(false);
  if (tools.length === 0) return null;

  return (
    <div className={`relative inline-block text-left ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="inline-flex min-h-[36px] items-center gap-2 rounded-full border border-border/60 bg-background/70 px-3 text-xs font-medium text-muted-foreground backdrop-blur transition-colors hover:text-foreground"
      >
        <span
          aria-hidden="true"
          className={`size-1.5 rounded-full ${
            availability === "native"
              ? "bg-emerald-500"
              : availability === "polyfill"
                ? "bg-amber-500"
                : "bg-muted-foreground/50"
          }`}
        />
        {tools.length} agent tools on this page
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-80 rounded-xl border border-border/60 bg-background/95 p-3 shadow-xl backdrop-blur">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
            {LABEL[availability] ?? LABEL.unknown}
          </p>
          <ul className="mt-2 max-h-64 space-y-1.5 overflow-y-auto">
            {tools.map(({ tool }) => (
              <li key={tool.name} className="text-xs">
                <code className="text-foreground">{tool.name}</code>
                <p className="text-muted-foreground">{tool.description}</p>
              </li>
            ))}
          </ul>
          <Link
            to="/webmcp"
            className="mt-3 inline-block text-xs font-medium underline underline-offset-4"
          >
            What is WebMCP?
          </Link>
        </div>
      )}
    </div>
  );
}
